// src/components/Hero.js
import React, { useState, useRef, useEffect } from 'react';

// ✅ CORRECTED: Use a single, consolidated import. This is the definitive fix.
import { ArrowUp, Instagram, Facebook, Twitter, Linkedin, Youtube, Film } from 'lucide-react';

import heroBg from '../assets/images/hero-background.png';
import AuthModal from './AuthModal';

const Hero = () => {
  const [platformTarget, setPlatformTarget] = useState('Instagram');
  const [prompt, setPrompt] = useState('');
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);
  
  const platformOptions = {
    'Instagram': { icon: Instagram, description: 'For Reels, Stories, and photo posts.' },
    'Facebook': { icon: Facebook, description: 'For posts, ads, and community updates.' },
    'X (Twitter)': { icon: Twitter, description: 'For threads and short-form updates.' },
    'LinkedIn': { icon: Linkedin, description: 'For articles and professional content.' },
    'YouTube': { icon: Youtube, description: 'For video descriptions and titles.' },
    'TikTok': { icon: Film, description: 'For viral video captions and trends.' },
  };

  const handlePromptSubmit = () => {
    if (prompt.trim()) {
      setIsModalOpen(true);
    }
  };

  const handleKeyDown = (event) => {
    if (event.key === 'Enter') {
      handlePromptSubmit();
    }
  };
  
  const SelectedIcon = platformOptions[platformTarget].icon;

  return (
    <>
      <section className="relative h-screen flex flex-col items-center justify-center text-center px-4 overflow-hidden">
        <div className="hero-bg-image-container">
          <img src={heroBg} alt="Abstract background" className="w-full h-full object-cover animate-pan-zoom" />
        </div>

        <div className="relative z-10">
          <h1 className="text-4xl sm:text-5xl md:text-6xl font-extrabold text-transparent bg-clip-text bg-gradient-to-br from-white to-gray-400 leading-tight">
            Generate a month of social posts <br /> in a single prompt.
          </h1>
          <p className="mt-6 max-w-2xl mx-auto text-lg text-dark-text-secondary">
            Describe your campaign, choose your platforms, and let Oolook’s AI craft an entire content calendar for you in seconds.
          </p>

          <div className="relative w-full max-w-2xl mx-auto mt-12">
            <div className="flex items-center w-full bg-black/30 backdrop-blur-lg border border-white/10 rounded-2xl shadow-2xl p-2">
              <div className="relative" ref={dropdownRef}>
                <button 
                  onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                  className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm text-dark-text-primary bg-white/5 hover:bg-white/10 transition-colors"
                >
                  <SelectedIcon size={16} />
                  <span>{platformTarget}</span>
                </button>
                <div className={`absolute bottom-full left-0 mb-3 w-72 bg-dark-card border border-white/10 rounded-lg shadow-2xl p-2 transition-all duration-200 ease-out ${isDropdownOpen ? 'opacity-100 scale-100' : 'opacity-0 scale-95 pointer-events-none'}`}>
                  {Object.entries(platformOptions).map(([key, value]) => (
                    <button 
                      key={key}
                      onClick={() => { setPlatformTarget(key); setIsDropdownOpen(false); }}
                      className="w-full flex items-start gap-3 text-left p-3 rounded-md hover:bg-white/5 transition-colors"
                    >
                      <value.icon size={16} className="mt-1 text-dark-text-secondary flex-shrink-0" />
                      <div>
                        <p className="text-sm font-semibold text-dark-text-primary">{key}</p>
                        <p className="text-xs text-dark-text-secondary">{value.description}</p>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
              <input
                type="text"
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Ask Oolook to create a campaign for..."
                className="w-full bg-transparent text-dark-text-primary placeholder:text-dark-text-secondary/50 border-none outline-none px-4 text-lg"
              />
              <button 
                onClick={handlePromptSubmit}
                className="flex-shrink-0 w-10 h-10 flex items-center justify-center bg-white/10 rounded-full hover:bg-white/20 transition-colors disabled:opacity-50"
                disabled={!prompt}
              >
                <ArrowUp size={20} className="text-dark-text-primary" />
              </button>
            </div>
          </div>
        </div>
      </section>

      <AuthModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} />
    </>
  );
};

export default Hero;